import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
// import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { PersistGate } from 'redux-persist/integration/react';
import { FirebaseAuthServiceErrorCode, auth, getJWTToken } from "../Services/FirebaseService";
import { AppDispatch, persistor } from "../Redux/store";
import ApiService from "../Services/ApiService";
import Teachr from "../Objects/models/Teachr";
import { fetchImageProfile,  userTeachrSetAccountAction } from "../Redux/Actions/UserActions";
import DialogLayout from "../Components/DialogLayout";
// import { resetActiveCourseAction, resetInfoChartAction, resetNextCourseAction, resetPastCourseAction } from "../Redux/Actions/PunctualCourseActions";
import TeachrRepository from "../Repositories/TeachrRepository";
import ApiVersionRepository from "../Repositories/ApiVersionRepository";
import {  getActiveBrandConfig } from "../config/environment.config";
import BackdropLoad from "../Components/BackdropLoad";
import { useAuthFn } from "../Hooks/Auth/useAuthFn";
import UserStore from "../LocalForage/UserStore";
import { useTailwindBrand } from "../Components/shared";
import { CATEGORY_ICONS } from "../Pages/App/WhatsNewPage";
import {
    APP_VERSION,
    VERSION_KEY,
    VersionInfo,
    WHATS_NEW_PATH,
    WHATS_NEW_PENDING_KEY,
    apiVersionKey,
    changelogCategory,
    highlightedEntries,
    seeAllLabel,
} from "../Utils/changelogView";
// import { resetActiveClients, resetAllClients } from "../Redux/Actions/StudentActions";

const TOKEN_EXPIRY_ADVANCE_MS = 10_000;

const RootLoaderGate = ({ children }: { children: React.ReactNode }) => {
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [isConnect, setIsConnect] = useState(false);
    const [showVersionDialog, setShowVersionDialog] = useState(false);

    // Détection synchrone du changement de version (localStorage est synchrone)
    // → évite la race condition d'un useRef mis à jour dans un useEffect.
    const [hasVersionMismatch] = useState(() => {
        const storedVersion = localStorage.getItem(VERSION_KEY);
        if (storedVersion === null) {
            localStorage.setItem(VERSION_KEY, APP_VERSION);
            return false;
        }
        return storedVersion !== APP_VERSION;
    });

    // useRef pour éviter les race conditions (setState est asynchrone)
    const tokenRefreshInProgress = useRef(false);

    // const setLoading = useCallback((value: boolean) => setLoad(value), []);
    const handleOpen = useCallback(() => setOpen(true), []);
    const handleClose = useCallback(() => setOpen(false), []);

    const { performSignOut } = useAuthFn({ setLoading });
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [user] = useAuthState(auth);
    const route = getActiveBrandConfig();
    const location = useLocation();
    const pathLogin = location.pathname === "/";
    const alreadyOnDashboard = location.pathname.includes(`/${route}/tableau-de-bord/`);
    // Parcours client par lien magique (ancien "complete-infos" + nouveau "inscription-aici") :
    // même traitement (déconnexion d'un éventuel prof, pas de redirection du visiteur).
    const pathClientCompleteInfos = location.pathname.includes("/client/complete-infos/") || location.pathname.includes("/client/inscription-aici/");
    const currentUser = useSelector((state: any) => state?.user?.currentUser);
    const isTeachr = currentUser?.isTeachr || false;
    const isClient = currentUser?.isClient || false;
    const teachr : Teachr | null = currentUser?.teachr || null;
    const client = currentUser?.client;
    const isFullyValidatedTeachr = isTeachr && teachr ? teachr.isValidated && teachr.isAE : false;

    const handleDisconnect = useCallback(() => {
        performSignOut(isClient ? false : true)
        handleClose();
    }, [handleClose, isClient, performSignOut]);

    const handleVersionUpdate = useCallback(async () => {
        localStorage.setItem(VERSION_KEY, APP_VERSION);
        setShowVersionDialog(false);
        await performSignOut(isClient ? false : true);
        navigate('/');
    }, [performSignOut, isClient, navigate]);

    // « Voir les nouveautés » : même reconnexion que le bouton, mais la page Quoi de neuf
    // s'ouvre dès que le compte est rechargé (marqueur lu par l'effet ci-dessous).
    const handleSeeWhatsNew = useCallback(async () => {
        localStorage.setItem(WHATS_NEW_PENDING_KEY, APP_VERSION);
        await handleVersionUpdate();
    }, [handleVersionUpdate]);

    useEffect(() => {
        if (!isTeachr) return;
        if (localStorage.getItem(WHATS_NEW_PENDING_KEY) !== APP_VERSION) return;
        localStorage.removeItem(WHATS_NEW_PENDING_KEY);
        navigate(WHATS_NEW_PATH());
    }, [isTeachr, navigate]);

    // Récupération instantanée des infos de version via React Query
    // (mise en cache + re-render automatique dès l'arrivée des données,
    //  contrairement à un setState dans un useEffect qui nécessitait un refresh).
    const { data: versionInfo } = useQuery<VersionInfo>({
        queryKey: ['app-version', APP_VERSION],
        queryFn: () => ApiVersionRepository.getVersion(apiVersionKey(APP_VERSION)),
        enabled: hasVersionMismatch,
        staleTime: Infinity,
        retry: false,
    });
    // Quoi de neuf : réservé aux profs déjà inscrits avant la mise à jour (le marqueur de version
    // n'existe pas pour un nouveau compte). Les clients et élèves ne voient que le message de reconnexion.
    const highlights = isTeachr ? highlightedEntries(versionInfo?.changelog) : [];
    const changelogCount = isTeachr ? (versionInfo?.changelog?.length ?? 0) : 0;
    const { brandConfig } = useTailwindBrand();
    const brandPrimary = brandConfig.colors?.primary ?? "#0054A3";

    // Effet de bord : purge du store persisté puis affichage du dialogue de mise à jour
    useEffect(() => {
        if (!hasVersionMismatch) return;
        persistor.purge().then(() => {
            setShowVersionDialog(true);
            setLoading(false);
        });
    }, [hasVersionMismatch]);

    // Ref vers la dernière version de refreshToken pour éviter les closures périmées dans setTimeout
    const doRefreshRef = useRef<() => Promise<void>>(async () => {});
    const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Planifie le prochain refresh précisément 10s avant l'expiration du token
    const scheduleNextRefresh = useCallback(() => {
        if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
        const stsTokenManager = auth.currentUser?.stsTokenManager;
        if (!stsTokenManager) return;
        const delayMs = Math.max(stsTokenManager.expirationTime - Date.now() - TOKEN_EXPIRY_ADVANCE_MS, 0);
        refreshTimerRef.current = setTimeout(() => doRefreshRef.current(), delayMs);
    }, []);

    const refreshToken = useCallback(async () => {
        if (tokenRefreshInProgress.current || !user) return;
        tokenRefreshInProgress.current = true;
        try {
            const jwtToken = await getJWTToken(user, true);
            if (!jwtToken) return;

            if (isTeachr && teachr?.id) {
                ApiService.enableAuth(undefined, undefined, `teachr ${jwtToken}`);
                if (teachr.profilePicture?.id) {
                    dispatch(fetchImageProfile(teachr.profilePicture.id));
                }
            } else if (isClient && client) {
                ApiService.enableAuth(undefined, undefined, `client ${jwtToken}`);
            }
        } catch (error: any) {
            if (error?.code === FirebaseAuthServiceErrorCode.NETWORK_REQUEST_FAILED) {
                setIsConnect(true);
                handleOpen();
            } else if (
                error?.code === FirebaseAuthServiceErrorCode.USER_TOKEN_EXPIRED ||
                error?.code === FirebaseAuthServiceErrorCode.INVALID_USER_TOKEN
            ) {
                handleOpen();
            }
        } finally {
            tokenRefreshInProgress.current = false;
            scheduleNextRefresh();
        }
    }, [user, isTeachr, teachr, isClient, client, dispatch, handleOpen, scheduleNextRefresh]);

    // Garde doRefreshRef à jour avec la dernière version de refreshToken
    useEffect(() => { doRefreshRef.current = refreshToken; }, [refreshToken]);

    // Lance la planification dès que l'utilisateur est connecté
    useEffect(() => {
        if (!user) return;
        scheduleNextRefresh();
        return () => { if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current); };
    }, [user, scheduleNextRefresh]);

    const pageToOpen = useCallback(async () => {
        try {
            if (!user) return 
        
            const jwtToken = await getJWTToken(user);
            const userLocalForage = await UserStore.getItem("authorization");

            if (isClient && client) {
                if (client?.id) {
                    ApiService.enableAuth(undefined, undefined, `client ${jwtToken}`);
                } else {
                    navigate(`/${getActiveBrandConfig()}/register?=client`);
                }
                return;
            }

            if (isTeachr && teachr) {
                // Cas spécial : un teachr tente d'accéder à une page client → déconnexion
                if (pathClientCompleteInfos) {
                    await performSignOut();
                    navigate(location.pathname);
                    return;
                }

                if (teachr?.id) {
                    ApiService.enableAuth(undefined, undefined, `teachr ${jwtToken}`);

                    const refreshedUser = await TeachrRepository.getCurrentTeachr();
                    if (refreshedUser) {
                        const serializedTeachr = JSON.parse(JSON.stringify(refreshedUser));
                        dispatch(userTeachrSetAccountAction(serializedTeachr));
                    }

                    if (isFullyValidatedTeachr) {
                        if (!alreadyOnDashboard) navigate(`/${route}/tableau-de-bord/`);
                    }
                }
                return;
            }

            // Utilisateur Firebase connecté mais sans profil teachr/client en
            // base : inscription abandonnée à l'étape 1. On garde la session
            // et on le laisse reprendre à l'étape 2 sur /register.
            const pathRegister = location.pathname.includes("/register");
            if (currentUser && !pathClientCompleteInfos && !pathLogin && !pathRegister) {
                navigate(`/${getActiveBrandConfig()}/register`);
                return;
            }

            if ((userLocalForage as string)?.includes("client") && !pathClientCompleteInfos) {
                await performSignOut();
                navigate("/");
            }


        } catch (errors) {
            const error = JSON.parse(JSON.stringify(errors));
            console.error("Error during page initialization:", error);
        }
    }, [user, isClient, client, isTeachr, teachr, currentUser, pathClientCompleteInfos, pathLogin, navigate, performSignOut, location.pathname, isFullyValidatedTeachr, dispatch, alreadyOnDashboard, route]);

    useEffect(() => {
        if (hasVersionMismatch) return;
        const initializeApp = async () => {
            await pageToOpen();
            setLoading(false);
        };
        initializeApp();
    }, [setLoading, user]);

    return (
        <PersistGate
            loading={<BackdropLoad />}
            persistor={persistor}
            onBeforeLift={pageToOpen}
        >
            {!loading && (
                showVersionDialog ? (
                    <DialogLayout
                        title="Nouvelle version disponible"
                        text={`La version ${APP_VERSION} est en ligne. Pour en profiter, reconnectez-vous : vos données sont conservées.`}
                        valid="Se reconnecter"
                        cancel=""
                        open={showVersionDialog}
                        customIcon="hidden"
                        customCancel="hidden"
                        customValid="w-full col-span-2"
                        handleClose={handleVersionUpdate}
                        handleSubmit={handleVersionUpdate}
                    >
                        {highlights.length > 0 && (
                            <div className="mt-4 space-y-3">
                                {/* Points forts seulement (champ highlight du changelog, table api_version) :
                                    le détail complet est sur la page Quoi de neuf. */}
                                {highlights.map((entry, i) => (
                                    <div key={i} className="flex gap-3 items-start">
                                        <span
                                            className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full"
                                            style={{ backgroundColor: `${brandPrimary}14`, color: brandPrimary }}
                                        >
                                            {CATEGORY_ICONS[changelogCategory(entry)]}
                                        </span>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">{entry.title}</p>
                                            <p className="text-sm text-gray-500">{entry.description}</p>
                                        </div>
                                    </div>
                                ))}
                                {changelogCount > highlights.length && (
                                    <p className="text-sm pt-1">
                                        <button
                                            type="button"
                                            onClick={handleSeeWhatsNew}
                                            className="font-semibold underline underline-offset-2"
                                            style={{ color: brandPrimary }}
                                        >
                                            {seeAllLabel(changelogCount)}
                                        </button>
                                        <span className="text-gray-400"> · aussi dans votre menu, « Quoi de neuf »</span>
                                    </p>
                                )}
                            </div>
                        )}
                    </DialogLayout>
                ) : open ? (
                    <DialogLayout
                        title={isConnect ? "Connexion Internet Requise" : "Vous avez été déconnecté"}
                        text={
                            isConnect
                                ? "Vous devez être connecté à Internet pour accéder à cette page. Veuillez vérifier votre connexion, puis actualisez la page pour continuer."
                                : "Vous avez été déconnecté de votre session. Les sessions de tous les onglets ont été déconnectées."
                        }
                        valid="Se connecter à nouveau"
                        cancel={""}
                        open={open}
                        customIcon="hidden"
                        customCancel="hidden"
                        customValid="w-full col-span-2"
                        handleClose={handleDisconnect}
                        handleSubmit={handleDisconnect}
                    />
                ) : (
                    children
                )
            )}
        </PersistGate>
    );
};

export default RootLoaderGate;
